<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

const props = defineProps<{
  error?: string;
}>();

let cardElement: any;
let elements: any;
let stripe: any;
let stripePromise: Promise<any> | null = null;

const cardError = ref('');
const cardHolderName = ref('');
const creditCardToken = ref<any>('');
const showProgressBar = ref(false);

const model = defineModel({
  default: {
    cardHolderName: '',
    creditCardToken: '',
  },
});

const showCardError = computed(() => cardError.value || (!!props.error && !creditCardToken.value));
const _model = computed(() => {
  return {
    cardHolderName: cardHolderName.value,
    creditCardToken: creditCardToken.value,
  };
});

onMounted(() => setupStripe());

watch(_model, () => (model.value = _model.value));

// HELPERS
async function setupStripe() {
  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
  }

  stripe = await stripePromise;
  elements = stripe.elements();
  cardElement = elements.create('card', { style: { base: { fontSize: '14px' } } });

  const debouncedValidateCard = useDebounceFn(() => {
    onValidateCard();
  }, 500);

  cardElement.on('change', () => debouncedValidateCard());

  cardElement.mount('#card-element');
}

async function onValidateCard() {
  showProgressBar.value = true;
  cardError.value = '';

  const { token, error: stripeError } = await stripe.createToken(cardElement, {
    name: cardHolderName.value,
  });

  if (stripeError) {
    cardError.value = stripeError.message;
    showProgressBar.value = false;
    return;
  }

  creditCardToken.value = token;
  showProgressBar.value = false;
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="relative flex w-full flex-col">
      <AFieldLabel
        required
        :error="error && !cardHolderName"
        :label="$t('common.name')"
      />

      <AFieldInputText
        v-model="cardHolderName"
        required
        :error="error && !cardHolderName"
        :placeholder="$t('cart.paymentMethod.validateName')"
      />
    </div>

    <div class="relative flex w-full flex-col">
      <AFieldLabel
        required
        :error="showCardError"
        :label="$t('cart.label.creditCard')"
      />

      <div
        id="card-element"
        :class="[
          'rounded-md border border-slate-300 px-3 py-2',
          showCardError ? '!placeholder:text-red-600 !border-red-600' : 'border-slate-300',
        ]"
      ></div>

      <AFieldError
        v-if="!showProgressBar"
        :error="cardError"
      />

      <div
        v-if="showProgressBar"
        class="flex h-5 items-center gap-2"
      >
        <AFieldLabel
          class="text-xs text-slate-500"
          :label="$t('cart.paymentMethod.validating')"
        />

        <ProgressBar
          class="h-0.5 w-full"
          mode="indeterminate"
          pt:value="bg-brand-400"
        />
      </div>
    </div>
  </div>
</template>
